class GfStickyBar extends HTMLElement {
  connectedCallback() {
    const controller = new AbortController();
    const { signal } = controller;
    const timeouts = [];
    this._controller = controller;
    this._timeouts = timeouts;

    const hideSel = this.dataset.hideSelector;
    // A merchant-entered selector can be invalid CSS. Don't let that throw
    // and abort the rest of connectedCallback, leaving the bar hidden forever.
    let target = null;
    if (hideSel) {
      try {
        target = document.querySelector(hideSel);
      } catch (err) {
        target = null;
      }
    }
    const hero = document.querySelector('.gf-section');
    const wrapper = document.querySelector('.page-wrapper');

    // The footer (Oct 2026). The bar is fixed to the bottom of the screen, so
    // at the end of the page it sat on top of the footer's last lines - the
    // policy links, the legal notice and the credit - and they could not be
    // read or tapped. The bar now stands down while any part of the footer is
    // on screen; the same button is never more than a scroll away.
    const footerInner = document.querySelector('.gf-footer');
    const footer =
      (footerInner && (footerInner.closest('footer') || footerInner.closest('.gf-section'))) ||
      document.querySelector('footer');

    this.hidden = false;

    const show = (on) => (on ? this.setAttribute('data-shown', '') : this.removeAttribute('data-shown'));

    // Horizon only makes `.page-wrapper` a scroll container at >=990px (see
    // base.css). Below that breakpoint it has no overflow constraint, so its
    // intersection rect spans the whole document and observers rooted on it
    // never fire correctly. Resolve the root dynamically instead of assuming,
    // and re-check on resize since crossing the breakpoint flips the answer.
    const isScrollable = (el) => {
      if (!el) return false;
      const oy = getComputedStyle(el).overflowY;
      return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1;
    };

    let pastHero = false;
    let offerVisible = false;
    let footerVisible = false;
    let consentOpen = false;
    let observerReported = false;
    let fallbackActive = false;
    let heroObserver = null;
    let targetObserver = null;
    let footerObserver = null;
    let root = isScrollable(wrapper) ? wrapper : null;

    const apply = () => show(pastHero && !offerVisible && !footerVisible && !consentOpen);

    const sync = () => {
      // Once the scroll fallback has taken over, a late-firing observer
      // callback must not fight it for control of the bar's visibility.
      if (fallbackActive) return;
      apply();
    };

    // Shopify's consent banner is fixed to the bottom at z-index 2000000, so
    // on a phone it sits directly on top of this bar and swallows every tap on
    // the CTA. Stand down until consent is resolved rather than stacking two
    // bars on top of each other: the banner has to be dealt with first anyway,
    // and the same CTA is a few hundred pixels further down the page.
    const CONSENT_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs';

    // Dismissing the banner sets `style="display:none"` on it rather than
    // removing it from the DOM, so presence is not the same as being on
    // screen. getClientRects() is empty for a display:none element, which
    // keeps this honest without reading layout-forcing properties.
    const consentIsOpen = () => {
      for (const el of document.querySelectorAll(CONSENT_SELECTOR)) {
        if (el.getClientRects().length > 0) return true;
      }
      return false;
    };

    const watched = new WeakSet();
    // Declared before syncConsent so the callback can close over it.
    const attributeObserver = new MutationObserver(() => syncConsent());

    const syncConsent = () => {
      // The banner is hidden by attribute, not by removal, so each consent
      // element needs its own attribute observer once it appears.
      for (const el of document.querySelectorAll(CONSENT_SELECTOR)) {
        if (watched.has(el)) continue;
        watched.add(el);
        attributeObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
      const open = consentIsOpen();
      if (open === consentOpen) return;
      consentOpen = open;
      apply();
    };

    // Both the banner and the preferences dialog are injected as direct
    // children of <body>, so childList alone catches their arrival — no
    // subtree walk on every DOM change in the page.
    const consentObserver = new MutationObserver(syncConsent);
    consentObserver.observe(document.body, { childList: true });
    this._consentObservers = [consentObserver, attributeObserver];
    syncConsent();

    const supportsIO = typeof IntersectionObserver === 'function';

    const disconnectObservers = () => {
      if (heroObserver) {
        heroObserver.disconnect();
        heroObserver = null;
      }
      if (targetObserver) {
        targetObserver.disconnect();
        targetObserver = null;
      }
      if (footerObserver) {
        footerObserver.disconnect();
        footerObserver = null;
      }
    };
    this._disconnectObservers = disconnectObservers;

    const createObservers = () => {
      disconnectObservers();
      if (!supportsIO) return;
      if (hero) {
        heroObserver = new IntersectionObserver(([entry]) => {
          observerReported = true;
          pastHero = !entry.isIntersecting;
          sync();
        }, { root });
        heroObserver.observe(hero);
      }
      if (target) {
        targetObserver = new IntersectionObserver(([entry]) => {
          observerReported = true;
          offerVisible = entry.isIntersecting;
          sync();
        }, { root, rootMargin: '0px 0px -20% 0px' });
        targetObserver.observe(target);
      }
      if (footer) {
        footerObserver = new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          sync();
        }, { root });
        footerObserver.observe(footer);
      }
    };

    // Fallback: if IntersectionObserver is unavailable, or no observer
    // callback has reported within 1000ms, drive the same booleans from
    // getBoundingClientRect() on a scroll listener instead. This guarantees
    // the bar can't silently stay hidden forever because of an observer that
    // never initialised (e.g. a root that turned out not to be scrollable).
    const engageFallback = () => {
      if (fallbackActive) return;
      fallbackActive = true;
      disconnectObservers();

      const scrollRoot = root || window;
      const computeFromRects = () => {
        const viewportHeight = window.innerHeight;
        const heroRect = hero ? hero.getBoundingClientRect() : null;
        const targetRect = target ? target.getBoundingClientRect() : null;
        const footerRect = footer ? footer.getBoundingClientRect() : null;
        pastHero = heroRect ? heroRect.bottom <= 0 : false;
        offerVisible = targetRect ? targetRect.top < viewportHeight * 0.8 && targetRect.bottom > 0 : false;
        footerVisible = footerRect ? footerRect.top < viewportHeight && footerRect.bottom > 0 : false;
        apply();
      };

      scrollRoot.addEventListener('scroll', computeFromRects, { passive: true, signal });
      computeFromRects();
    };

    // `observerReported` alone isn't proof the observer got it right: a root
    // that silently isn't scrollable still fires once with a stale verdict.
    // So also engage the fallback if, 1000ms after that report, `pastHero` is
    // still false while the hero has plainly scrolled off-screen — reality
    // disagreeing with the observer means the observer can't be trusted.
    const armWatchdog = () => {
      observerReported = false;
      const id = setTimeout(() => {
        if (!observerReported) {
          engageFallback();
          return;
        }
        const heroRect = hero ? hero.getBoundingClientRect() : null;
        if (!pastHero && heroRect && heroRect.bottom < 0) {
          engageFallback();
        }
      }, 1000);
      timeouts.push(id);
    };

    if (!supportsIO) {
      engageFallback();
    } else {
      createObservers();
      armWatchdog();
    }

    // Crossing the 990px breakpoint changes whether `.page-wrapper` is
    // actually scrollable. Re-evaluate on resize and rebuild the observers
    // against the new root when the verdict flips.
    const handleResize = () => {
      if (fallbackActive) return;
      const nowScrollable = isScrollable(wrapper);
      const newRoot = nowScrollable ? wrapper : null;
      if (newRoot === root) return;
      root = newRoot;
      createObservers();
      armWatchdog();
    };
    window.addEventListener('resize', handleResize, { passive: true, signal });
  }

  disconnectedCallback() {
    // Every theme-editor re-render tears down and reconnects this element.
    // Without this, observers, the watchdog timeout, and the scroll/resize
    // listeners from the previous connection would all leak.
    if (this._controller) {
      this._controller.abort();
      this._controller = null;
    }
    if (this._timeouts) {
      this._timeouts.forEach(clearTimeout);
      this._timeouts = null;
    }
    if (this._disconnectObservers) {
      this._disconnectObservers();
      this._disconnectObservers = null;
    }
    if (this._consentObservers) {
      this._consentObservers.forEach((observer) => observer.disconnect());
      this._consentObservers = null;
    }
  }
}

if (!customElements.get('gf-sticky-bar')) customElements.define('gf-sticky-bar', GfStickyBar);
